import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, Package, X } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { getCategories, getSearchSuggestions, getProducts } from '../services/api';
import { motion } from 'framer-motion';

const Products = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [suggestions, setSuggestions] = useState({ products: [], categories: [] });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await getCategories();
        setCategories(res.data || []);
      } catch { }
      finally { setLoading(false); }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchChange = (value) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length < 2) {
      setSuggestions({ products: [], categories: [] });
      setShowSuggestions(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await getSearchSuggestions(value);
        setSuggestions(res.data);
        setShowSuggestions(true);
      } catch { }
    }, 300);
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    setShowSuggestions(false);
    if (!search.trim()) {
      setSearchResults(null);
      return;
    }
    try {
      const res = await getProducts({ search: search.trim() });
      setSearchResults(res.data);
    } catch { }
  };

  const clearSearch = () => {
    setSearch('');
    setSearchResults(null);
    setSuggestions({ products: [], categories: [] });
    setShowSuggestions(false);
  };

  const getImageUrl = (img) => {
    if (!img) return null;
    const url = typeof img === 'string' ? img : img?.url;
    if (!url) return null;
    return url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f8f9fb] to-white" data-testid="products-page">
      {/* Header */}
      <section className="bg-[#2E3A6F] text-white py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h1 className="text-4xl sm:text-5xl font-bold">Our Products</h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto">
            Precision cutting tools for every machining application
          </p>

          {/* Search Bar */}
          <div className="max-w-xl mx-auto relative" ref={searchRef}>
            <form onSubmit={handleSearch}>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onFocus={() => search.length >= 2 && setShowSuggestions(true)}
                  placeholder="Search products, categories, SKU..."
                  className="w-full pl-12 pr-12 py-4 rounded-xl bg-white text-gray-900 text-base placeholder:text-gray-400 focus:ring-4 focus:ring-white/30 outline-none shadow-lg"
                  data-testid="product-search-input"
                />
                {search && (
                  <button type="button" onClick={clearSearch} className="absolute right-14 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <X size={18} />
                  </button>
                )}
                <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 bg-[#2E3A6F] text-white p-2 rounded-lg hover:bg-[#1f2747] transition-colors" data-testid="product-search-btn">
                  <Search size={18} />
                </button>
              </div>
            </form>

            {/* Auto-suggestions Dropdown */}
            {showSuggestions && (suggestions.products?.length > 0 || suggestions.categories?.length > 0) && (
              <div className="absolute top-full mt-2 w-full bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50" data-testid="search-suggestions">
                {suggestions.categories?.length > 0 && (
                  <div className="px-4 py-2 border-b">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Categories</p>
                    {suggestions.categories.map(cat => (
                      <Link
                        key={cat.id}
                        to={`/category/${cat.id}`}
                        onClick={() => setShowSuggestions(false)}
                        className="flex items-center gap-3 py-2 hover:bg-gray-50 rounded-lg px-2 transition-colors"
                      >
                        <div className="w-8 h-8 rounded-lg bg-[#2E3A6F]/10 flex items-center justify-center overflow-hidden">
                          {cat.image ? (
                            <img src={getImageUrl(cat.image)} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Package size={14} className="text-[#2E3A6F]" />
                          )}
                        </div>
                        <span className="text-sm text-gray-900 font-medium">{cat.name}</span>
                        <ArrowRight size={14} className="ml-auto text-gray-400" />
                      </Link>
                    ))}
                  </div>
                )}
                {suggestions.products?.length > 0 && (
                  <div className="px-4 py-2">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Products</p>
                    {suggestions.products.map(prod => (
                      <Link
                        key={prod.id}
                        to={`/product/${prod.id}`}
                        onClick={() => setShowSuggestions(false)}
                        className="flex items-center gap-3 py-2 hover:bg-gray-50 rounded-lg px-2 transition-colors"
                      >
                        <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden">
                          {prod.images?.length > 0 ? (
                            <img src={getImageUrl(prod.images[0])} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Package size={14} className="text-gray-400" />
                          )}
                        </div>
                        <div>
                          <span className="text-sm text-gray-900 font-medium block">{prod.name}</span>
                          {prod.sku && <span className="text-[10px] text-gray-400">SKU: {prod.sku}</span>}
                        </div>
                        <ArrowRight size={14} className="ml-auto text-gray-400" />
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Search Results */}
      {searchResults && (
        <section className="py-12">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                Search Results for "{search}" ({searchResults.total} found)
              </h2>
              <Button variant="outline" onClick={clearSearch} size="sm">Clear Search</Button>
            </div>
            {searchResults.products?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {searchResults.products.map((product) => (
                  <Link key={product.id} to={`/product/${product.id}`}>
                    <Card className="group hover:shadow-xl transition-all duration-300 border-0 bg-white overflow-hidden h-full">
                      <div className="relative overflow-hidden">
                        {product.images?.length > 0 ? (
                          <img src={getImageUrl(product.images[0])} alt={product.name} className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = 'https://placehold.co/600x400/e5e7eb/6b7280?text=No+image'; }} />
                        ) : (
                          <div className="w-full h-48 bg-gradient-to-br from-[#2E3A6F] to-[#1a1f3a] flex items-center justify-center"><Package className="text-white/30" size={40} /></div>
                        )}
                      </div>
                      <CardContent className="p-5 space-y-2">
                        <h3 className="font-semibold text-gray-900 group-hover:text-[#2E3A6F] transition-colors">{product.name}</h3>
                        <p className="text-sm text-gray-600 line-clamp-2">{product.description}</p>
                        <div className="flex items-center text-[#2E3A6F] text-sm font-medium pt-2">View Details <ArrowRight className="ml-1" size={14} /></div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            ) : (
              <Card className="border-dashed"><CardContent className="p-12 text-center text-gray-500">No products found for "{search}"</CardContent></Card>
            )}
          </div>
        </section>
      )}

      {/* Categories Grid */}
      {!searchResults && (
        <section className="py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Product Categories</h2>
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => <Card key={i} className="animate-pulse"><CardContent className="p-0 h-72" /></Card>)}
              </div>
            ) : categories.length === 0 ? (
              <Card className="border-dashed"><CardContent className="p-12 text-center text-gray-500">No categories available yet.</CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {categories.map((category, index) => (
                  <motion.div
                    key={category.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Link to={`/category/${category.id}`} data-testid={`category-link-${category.id}`}>
                      <Card className="group overflow-hidden border-0 bg-white hover:shadow-2xl transition-all duration-500 h-full">
                        <div className="relative overflow-hidden h-48">
                          {category.image ? (
                            <img src={getImageUrl(category.image)} alt={category.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-[#2E3A6F] to-[#1a1f3a] flex items-center justify-center">
                              <Package className="text-white/30" size={48} />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                          <div className="absolute bottom-4 left-4">
                            <h3 className="text-xl font-bold text-white">{category.name}</h3>
                          </div>
                        </div>
                        <CardContent className="p-5 space-y-3">
                          <p className="text-sm text-gray-600 line-clamp-2">{category.description}</p>
                          <div className="flex items-center text-[#2E3A6F] font-medium group-hover:translate-x-2 transition-transform duration-300">
                            Explore Products <ArrowRight className="ml-2" size={18} />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default Products;
